import { useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { EmptyState, LoadingState, usePresence, useToast } from '@platform/ui-kit'
import { ApiErrorState } from '@/app/errors/ApiErrorState'
import { useAuth } from '@/app/providers/AuthProvider'
import { chatApi } from './api'
import { setChatUnread } from './unreadStore'
import { parseChatNotification, showChatNotification } from './chatNotification'
import { CHAT_ATTEMPTS_KEY, registerChatSignOut } from './chatSession'
import { createChatRpcBridge } from './rpc/bridge'
import { OPEN_NAV_EVENT } from './shellEvents'
import { switchTheme } from '@/app/shell/theme'

/* ------------------------------------------------------------------
   گفتگو — Rocket.Chat در قابی که با جابه‌جاییِ صفحه‌ها نمی‌میرد
   ------------------------------------------------------------------
   🔴 چرا اینجا و نه در خودِ صفحهٔ `/app/chat`.

   قاب داخلِ صفحه بود؛ رفتن به صفحهٔ دیگر آن را از DOM برمی‌داشت و
   برگشتن، کلِ Rocket.Chat را از صفر بار می‌کرد — چند ثانیه و یک
   ورودِ دوباره، هر بار. حالا یک بار، پس از اولین بازدید، در قالبِ
   پنل ساخته می‌شود و بیرون از گفتگو فقط پنهان (`hidden`) است.

   ⚠️ قاب هرگز جابه‌جا نمی‌شود — جابه‌جاییِ iframe در DOM همان
   بارگذاریِ دوباره است. فقط نمایانی‌اش عوض می‌شود.

   ------------------------------------------------------------------
   🔴 ورود: Rocket.Chat خودش می‌گوید وارد هست یا نه.

   دو «Custom Script» در Rocket.Chat (Layout → Custom Scripts) به این
   صفحه پیام می‌دهند: `{carbon:'logged-out'}` و `{carbon:'logged-in'}`.
   با `logged-out` ورودِ OAuth شروع می‌شود؛ تا `logged-in` نیاید،
   پوششِ «در حال ورود» رویِ قاب است تا صفحهٔ ورودِ Rocket.Chat
   اصلاً دیده نشود.

   ⚠️ اگر آن اسکریپت‌ها تنظیم نشده باشند، رفتارِ قبلی می‌ماند: یک بار
   در هر زبانه فرمانِ ورود فرستاده می‌شود و پوشش پس از چند ثانیه کنار
   می‌رود. یعنی تنظیم‌نکردنشان چیزی را نمی‌شکند.

   ------------------------------------------------------------------
   🔴 «وارد هست» کافی نیست — باید همان کسی باشد که وارد پنل شده.

   Rocket.Chat جلسه‌اش را جدا از پنل در مرورگر نگه می‌دارد. دیده‌شده
   ۲۰۲۶-۱۰-۰۴: پس از خروجِ یک نفر و ورودِ نفرِ دیگر در همان مرورگر،
   گفتگو هنوز با حسابِ نفرِ اول باز می‌شد. پس `logged-in` ایمیل‌های
   کاربرِ Rocket.Chat را هم می‌آورد؛ اگر ایمیلِ کاربرِ پنل میانشان
   نبود، قاب خارج می‌شود و ورودِ OAuth با کاربرِ درست از نو شروع
   می‌شود. تا این بررسی تمام نشده، پوشش کنار نمی‌رود و عدد و اعلانی
   از قاب پذیرفته نمی‌شود — وگرنه همان لحظه گفتگوی دیگری لو می‌رفت.

   ⚠️ اسکریپتِ قدیمی که ایمیل نمی‌فرستد همان رفتارِ قبلی را دارد.

   ------------------------------------------------------------------
   🔴 کارها و پروژه‌ها: قاب از راهِ RPC از پنل می‌پرسد (`rpc/`).

   سایدبارِ چت کارها، تیم‌ها و پروژه‌ها را از API ِ پنل می‌خواهد ولی
   کوکی و CSRF ِ پنل را ندارد؛ پس `{carbonRpc}` می‌فرستد و این قاب با
   نشستِ پنل جواب می‌دهد. قرارداد در `rpc/contract.ts`.

   ⚠️ پاسخ فقط به همین قاب و فقط پس از بررسیِ ایمیل («verified») —
   اسکریپتِ قدیمیِ بی‌ایمیل قاب را باز می‌کند ولی RPC نمی‌گیرد.
   ------------------------------------------------------------------ */

const ATTEMPTS_KEY = CHAT_ATTEMPTS_KEY

/** خروجِ قاب منتظرِ تأییدِ Rocket.Chat می‌ماند، ولی نه بیشتر از این. */
const SIGN_OUT_WAIT_MS = 1500

/** سقفِ تلاشِ ورود در یک زبانه — اگر OAuth خطا بدهد، حلقه نمی‌سازد. */
const MAX_ATTEMPTS = 3

/** بی‌خبر از Rocket.Chat، پوشش پس از این مدت کنار می‌رود. */
const OVERLAY_FALLBACK_MS = 8000

/**
 * 🔴 ورودی که این مدت پس از `logged-out` به `logged-in` نرسید، گیر کرده است.
 *
 * ⚠️ دیده‌شده ۲۰۲۶-۱۰-۰۵: فرمانِ ورود رفت ولی OAuth در Rocket.Chat کامل
 * نشد، و پوششِ «در حال ورود» هر خطا یا دکمه‌ای را که Rocket.Chat نشان
 * می‌داد پنهان کرد — کاربر بی‌هیچ راهی گیر افتاد. پس از این مدت پوشش
 * کنار می‌رود و صفحهٔ خودِ Rocket.Chat دیده می‌شود. آن صفحه خارج‌شده
 * است، پس چیزی از کاربرِ دیگری نشان نمی‌دهد.
 */
const LOGIN_STALL_MS = 15000

/** بی‌خبر از `startup` ِ Rocket.Chat، فرمانِ ورود پس از این مدت به‌هرحال می‌رود. */
const READY_FALLBACK_MS = 4000

/** فرمانِ ورودی که تا این مدت به ورود نرسید، یک بارِ دیگر فرستاده می‌شود. */
const LOGIN_RETRY_MS = 6000

function attempts(): number {
  try {
    return Number(sessionStorage.getItem(ATTEMPTS_KEY) ?? '0') || 0
  } catch {
    return 0
  }
}

function countAttempt(): void {
  try {
    sessionStorage.setItem(ATTEMPTS_KEY, String(attempts() + 1))
  } catch {
    /* ذخیره‌سازیِ بسته — سقف در همین بارگذاری هم با شمارندهٔ حافظه نگه داشته می‌شود. */
  }
}

function resetAttempts(): void {
  try {
    sessionStorage.removeItem(ATTEMPTS_KEY)
  } catch {
    /* بی‌اثر */
  }
}

/**
 * ⚠️ قاب پس از این مدت در پس‌زمینه هم ساخته می‌شود — نه فقط با بازدیدِ
 * گفتگو. عددِ خوانده‌نشده‌های منو را خودِ Rocket.Chat از داخلِ قاب
 * می‌فرستد؛ بی قاب، منو تا اولین بازدیدِ گفتگو همیشه صفر نشان می‌داد.
 * تأخیر برای این است که بارگذاریِ چت با بارگذاریِ خودِ صفحه رقابت نکند.
 */
const BACKGROUND_DELAY_MS = 3000

/** اختلافِ بلندیِ کمتر از این، نوارِ ابزارِ مرورگر است نه صفحه‌کلید. */
const KEYBOARD_MIN_PX = 120

/**
 * قابِ گفتگو (57-owner-phone.js، فقط iOS) خبر می‌دهد که جای نوشتنی فوکوس گرفت یا رها شد —
 * یعنی صفحه‌کلید دارد باز یا بسته می‌شود. `detail`: `true` | `false`.
 */
const CHAT_KEYBOARD_EVENT = 'karbon:chat-keyboard'

/** پس از بسته شدنِ صفحه‌کلید تا این مدت صفحه‌ای که سافاری جابه‌جا گذاشته به بالا برمی‌گردد. */
const KEYBOARD_SETTLE_MS = 900

const chatConfigQuery = {
  queryKey: ['me', 'chat'],
  queryFn: chatApi.config,
  staleTime: Infinity,
} as const

export function ChatFrameHost() {
  const location = useLocation()
  const onChat = location.pathname === '/app/chat' || location.pathname.startsWith('/app/chat/')

  const config = useQuery(chatConfigQuery)

  /*
    🔴 پس‌زمینه فقط وقتی ورودِ خودکار تنظیم شده است — قابی که در
    پس‌زمینه پشتِ صفحهٔ ورود بماند هیچ عددی نمی‌فرستد و فقط بار است.
  */
  const canPreload = Boolean(config.data?.url && config.data?.sso_service)

  /* قاب یک بار ساخته می‌شود و تا خروج از پنل می‌ماند. */
  const [activated, setActivated] = useState(onChat)
  useEffect(() => {
    if (onChat) setActivated(true)
  }, [onChat])

  useEffect(() => {
    if (activated || !canPreload) return
    const timer = window.setTimeout(() => setActivated(true), BACKGROUND_DELAY_MS)
    return () => window.clearTimeout(timer)
  }, [activated, canPreload])

  if (!activated) return null

  return <ChatFrame visible={onChat} />
}

export function ChatFrame({ visible, standalone = false }: { visible: boolean; standalone?: boolean }) {
  const frameRef = useRef<HTMLIFrameElement>(null)
  const boxRef = useRef<HTMLDivElement>(null)
  const [signedIn, setSignedIn] = useState(false)
  const [mismatch, setMismatch] = useState(false)
  const [loginStalled, setLoginStalled] = useState(false)
  /*
    🔴 پردهٔ «در حال ورود به گفتگو» (۱۴۰۵/۰۸/۱۳): تا پوستهٔ گفتگو واقعاً آماده نشده می‌ماند و بعد محو می‌شود،
    نه یک‌باره (‎.pf-scrim‎ ِ tokens.css). «آماده» را خودِ قاب می‌گوید (‎carbon: 'ready'‎، 57-owner-phone.js) —
    ورود به‌تنهایی کافی نبود: Rocket.Chat پس از آن هنوز اسکلتِ سفیدش را نشان می‌داد. پوستهٔ قدیمی که این
    خبر را نمی‌دهد: ۱٫۵ ثانیه پس از ورود.
  */
  const [layoutReady, setLayoutReady] = useState(false)
  useEffect(() => {
    if (!signedIn || layoutReady) return
    const timer = window.setTimeout(() => setLayoutReady(true), 1500)
    return () => window.clearTimeout(timer)
  }, [signedIn, layoutReady])
  const loginOverlay = usePresence(!loginStalled && (!signedIn || !layoutReady), 240)
  const navigate = useNavigate()
  const toast = useToast()
  const { user } = useAuth()

  /* اثرِ گوش‌دادن فقط با عوض‌شدنِ سرور از نو ساخته می‌شود؛ نمایانی و
     ابزارها از راهِ ref خوانده می‌شوند تا شنونده کهنه نماند. */
  const panelEmail = user?.email?.trim().toLowerCase() || null
  const query = useQuery(chatConfigQuery)
  /* 🔴 ۱۴۰۵/۰۸/۰۵: کسی که ایمیل ندارد (یا ایمیلش در پنل عوض شده) هم «همان آدم» است —
     با شناسهٔ جفت‌شده یا نشانیِ جانشینش؛ پیش‌تر فقط ایمیل، و آن‌ها هرگز وارد نمی‌شدند. */
  const identity = query.data?.identity ?? null
  const expectedEmails = identity ? identity.emails.map((e) => e.toLowerCase()) : panelEmail ? [panelEmail] : []
  const expectedId = identity?.rocketchat_user_id ?? null
  const live = useRef({ visible, navigate, toast, expectedEmails, expectedId })
  live.current = { visible, navigate, toast, expectedEmails, expectedId }

  /*
    ==================================================================
    🔴 صفحه‌کلیدِ گوشی — یک قاعده برای همهٔ مرورگرها (۱۴۰۵/۰۸/۱۳)
    ==================================================================
    «هنوز تو مرورگر موقعی با گوشی کیبورد باز میشه نوبار میره بالا ثابت نیست؛ پیام‌ها و باکسِ چت بره
    بالا اوکیه ولی نوبار که اسم و عکس و وضعیتِ طرفه نباید بره بالا».

    ⚠️ کرومِ اندروید از نسخهٔ ۱۰۸ (و سامسونگ اینترنت، که همان کروم است) با باز شدنِ صفحه‌کلید خودِ صفحه
    را کوتاه نمی‌کند (پیش‌فرض: resizes-visual)؛ فقط «دید» کوتاه می‌شود و مرورگر کلِ صفحه را زیرِ آن بالا
    می‌بَرَد تا کادرِ پیام دیده شود — سرِ گفتگو همراهش از بالای صفحه بیرون می‌رفت. پیش‌تر درمانِ همین فقط
    برای فایرفاکس نوشته شده بود (۱۴۰۵/۰۸/۰۹، همان رفتار از نسخهٔ ۱۳۲) و برای آیفون جدا (پایین)؛ کروم
    «درست» فرض شده بود و نبود. حالا هر دو راه برای همه:

    ۱. interactive-widget=resizes-content — کروم، سامسونگ، فایرفاکس: صفحه با صفحه‌کلید کوتاه می‌شود،
       ‎h-dvh‎ ِ ظرف هم، و سرِ گفتگو بالا می‌ماند؛ کادرِ نوشتن درست روی صفحه‌کلید. WebKit این کلید را
       نمی‌شناسد.
    ۲. چسباندن به «دید» (visualViewport) — هر جا ۱ اثر نکرد: سافاری و کرومِ iOS همیشه، و مرورگری که
       کلید را نفهمد. تشخیص هندسی است، نه نامِ مرورگر: «دید» کوتاه‌تر از ریشه یعنی صفحه کوتاه نشده؛ وقتی
       ۱ کار کرده، این دو برابرند و این‌جا کاری نمی‌شود.
    خودِ قاب هم تا جای نوشتن فوکوس دارد سندش را سرِ جا نگه می‌دارد (57-owner-phone.js).

    ── آیفون (۱۴۰۵/۰۸/۰۷ «رو مرورگر سافاری اون قسمت کادر جستجو میاد رو باکس نوشتن پیام»، ۰۸/۱۰ «هنوز
    خوب کار نمیکنه»، ۰۸/۱۱ بسته شدن) — دو گرفتاری، هر دو فقط در WebKit ِ iOS:
    · بزرگ‌نمایی. سافاری با فوکوس روی ورودیِ ریزتر از ۱۶px کلِ صفحه را ۱۶ ÷ اندازهٔ قلم بزرگ می‌کند و
      برنمی‌گرداند (جعبهٔ پیامِ ۱۵px ِ Rocket.Chat: ۷٪)؛ صفحه کج و پایینش زیرِ نوارِ نشانیِ سافاری. سقفش را
      فقط متای همین صفحه تعیین می‌کند (متای قاب در WebKit نادیده است)، پس تا گفتگو باز است
      `maximum-scale=1` — فقط بزرگ‌نماییِ خودکار بسته می‌شود؛ دو انگشتی را سافاری (از iOS 10) و کرومِ iOS
      نگه می‌دارند. ⚠️ فقط iOS: اندروید همین سقف را برای دو انگشت هم جدی می‌گیرد (کفِ ۱۶px ِ tokens.css
      جای آن است)، و فقط تا گفتگو باز است (WKWebView ِ برخی برنامه‌ها دو انگشت را هم با آن می‌بندد).
    · صفحه‌کلید. سافاری نه صفحه را کوچک می‌کند، نه dvh را؛ فقط دید کوتاه می‌شود و صفحه را زیرِ آن
      جابه‌جا می‌کند (با overflow: hidden هم). تا صفحه‌کلید باز است ظرف درست روی همان دید می‌نشیند —
      fixed، از بالای دید (offsetTop) و به بلندیِ آن — و با هر جابه‌جایی دنبالش می‌رود؛ با بسته شدنِ
      صفحه‌کلید، بزرگ‌نماییِ دو انگشتی یا رفتن از گفتگو رها می‌شود. قاب جابه‌جا و از نو بار نمی‌شود.
    ⚠️ فقط ظرفِ تمام‌صفحه: گوشی، یا برنامهٔ نصبیِ گفتگو در هر پهنایی.
  */
  useEffect(() => {
    if (!visible || !window.matchMedia('(pointer: coarse)').matches) return

    const webkitIOS = window.CSS?.supports('-webkit-touch-callout', 'none') ?? false
    const meta = document.querySelector<HTMLMetaElement>('meta[name="viewport"]')
    const content = meta?.content
    if (meta && content) {
      if (webkitIOS) {
        if (!/maximum-scale/.test(content)) meta.content = `${content}, maximum-scale=1`
      } else if (!/interactive-widget/.test(content)) {
        meta.content = `${content}, interactive-widget=resizes-content`
      }
    }

    const view = window.visualViewport
    const fullScreen = standalone ? null : window.matchMedia('(max-width: 767px)')
    let pinned: HTMLDivElement | null = null
    let settle = 0
    /* آخرین خبرِ قاب (57-owner-phone.js): `false` یعنی هیچ جای نوشتنی فوکوس ندارد — صفحه‌کلید بسته است
       یا همین حالا دارد پایین می‌رود؛ `null` تا قاب چیزی نگفته (پوستهٔ قدیمی): فقط سنجشِ هندسی. */
    let frameKeyboard: boolean | null = null
    let settleUntil = 0
    let settleFrame = 0

    const release = () => {
      if (!pinned) return false
      for (const name of ['position', 'top', 'left', 'right', 'height', 'margin']) pinned.style.removeProperty(name)
      pinned = null
      return true
    }

    const fit = () => {
      window.clearTimeout(settle)
      const box = boxRef.current
      if (!view) return
      const unzoomed = view.scale < 1.01
      const full = fullScreen?.matches ?? true
      /* ⚠️ مبنا بلندیِ ریشه است (همان initial containing block)، نه innerHeight: صفحهٔ گفتگو درست یک صفحه
         بلند است و WebKit ‏innerHeight را در پایانِ سند می‌بُرد — وقتی خودش جعبهٔ پیام را وسط می‌برد،
         innerHeight تا بلندیِ دید پایین می‌آمد و چسباندن وسطِ باز شدنِ صفحه‌کلید رها می‌شد (پرش). نه
         صفحه‌کلید این بلندی را عوض می‌کند، نه جابه‌جاییِ سافاری؛ اگر سافاریِ آینده صفحه را برای صفحه‌کلید
         کوچک کند، این فاصله خودبه‌خود صفر می‌شود و این‌جا کاری نمی‌کند.
         🔴 ۱۴۰۵/۰۸/۱۱: وقتی قاب گفته جای نوشتن رها شده، بلندیِ کوتاهِ «دید» دیگر صفحه‌کلید حساب نمی‌شود —
         سافاری آن را گاهی پس از بسته شدن هم کهنه گزارش می‌کرد و ظرف نصفه می‌ماند. */
      const base = document.documentElement.clientHeight
      const keyboard = frameKeyboard !== false && full && unzoomed && base - view.height > KEYBOARD_MIN_PX
      if (!box || !keyboard) {
        /* تورِ ایمنی: سافاری گاهی صفحه را پس از بسته شدنِ صفحه‌کلید جابه‌جا می‌گذارد. ⚠️ نه فقط درست پس از
           رها کردن: اگر ظرف پیش‌تر رها شده بود و جابه‌جایی بعد رسید، صفحه همان‌جا نصفه می‌ماند (۱۴۰۵/۰۸/۱۱) */
        const was = release()
        if (unzoomed && full && (was || frameKeyboard === false) && (window.scrollY > 0 || view.offsetTop > 1)) window.scrollTo(0, 0)
        return
      }
      pinned = box
      box.style.position = 'fixed'
      box.style.left = '0'
      box.style.right = '0'
      box.style.margin = '0'
      box.style.top = `${view.offsetTop}px`
      box.style.height = `${view.height}px`
      /* سافاری (۱۵ تا ۲۶، باگِ WebKit ‏292603) با صفحه‌کلیدِ باز تا حدودِ ۱۰۰ نقطه بیش از پایانِ سند پایین
         می‌رود؛ آن‌جا «دید» هم در پایانِ سند بریده می‌شود و ظرف به آن نمی‌رسد — همان نوارِ سبزِ بالای
         صفحه‌کلید. کمی پس از آرام گرفتن، صفحه به بالا برمی‌گردد؛ دیده نمی‌شود، چون ظرف همراهِ دید است. */
      if (webkitIOS && view.offsetTop > 1) settle = window.setTimeout(() => window.scrollTo(0, 0), 250)
    }

    /*
      🔴 ۱۴۰۵/۰۸/۱۱ «وقتی کیبورد رو می‌بندیم صفحه چند لحظه خالی می‌مونه بعد تمام‌صفحه می‌شه». سافاری resize ِ
      «دید» را پس از پایین رفتنِ صفحه‌کلید می‌فرستد؛ تا آن موقع ظرف کوتاه می‌ماند و زیرش خالی بود. حالا با
      خبرِ قاب، همان لحظهٔ رها شدنِ جای نوشتن، ظرف تمام‌صفحه می‌شود و پایینِ گفتگو همراهِ پایین رفتنِ
      صفحه‌کلید پیدا می‌شود (مثلِ کرومِ اندروید). تا آرام گرفتنِ سافاری هر فریم دوباره سنجیده می‌شود.
    */
    const settleLoop = () => {
      settleFrame = 0
      if (frameKeyboard !== false || performance.now() > settleUntil) return
      fit()
      settleFrame = window.requestAnimationFrame(settleLoop)
    }
    const onKeyboard = (event: Event) => {
      frameKeyboard = (event as CustomEvent<boolean>).detail === true
      fit()
      if (frameKeyboard) return
      settleUntil = performance.now() + KEYBOARD_SETTLE_MS
      if (!settleFrame) settleFrame = window.requestAnimationFrame(settleLoop)
    }

    view?.addEventListener('resize', fit)
    view?.addEventListener('scroll', fit)
    fullScreen?.addEventListener('change', fit)
    window.addEventListener(CHAT_KEYBOARD_EVENT, onKeyboard)
    fit()

    return () => {
      window.clearTimeout(settle)
      window.cancelAnimationFrame(settleFrame)
      view?.removeEventListener('resize', fit)
      view?.removeEventListener('scroll', fit)
      fullScreen?.removeEventListener('change', fit)
      window.removeEventListener(CHAT_KEYBOARD_EVENT, onKeyboard)
      release()
      if (meta && content) meta.content = content
    }
  }, [visible, standalone])

  const url = query.data?.url ?? null
  const service = query.data?.sso_service ?? null

  useEffect(() => {
    if (!url) return

    const origin = new URL(url).origin
    const rpc = createChatRpcBridge({ origin, frame: () => frameRef.current?.contentWindow })
    let heardFromScripts = false
    let lastStart = 0
    let localAttempts = 0
    /** کاربرِ داخلِ قاب همان کاربرِ پنل است — تا آن وقت عدد و اعلان پذیرفته نمی‌شود. */
    let verified = false
    /** قابِ بی‌اسکریپتِ کاربن (نصبِ قدیمی) — پس از زمانِ پوشش همان رفتارِ قبلی. */
    let legacy = false
    /** آخرین عددِ خوانده‌نشده‌ای که پیش از تأیید رسید — Rocket.Chat فقط تغییر را می‌فرستد. */
    let pendingUnread: unknown = undefined
    let mismatches = 0
    let signedOut: (() => void) | null = null
    /*
      🔴 خروج از پنل در جریان است — ورودِ دوباره شروع نمی‌شود.

      ⚠️ اگر قاب پیش از برداشته‌شدن هنوز `logged-out` بفرستد و اینجا ورود
      شروع شود، OAuth با نشستِ هنوز زندهٔ پنل همان کاربرِ در حالِ خروج
      را دوباره وارد می‌کند.
    */
    let leaving = false
    let stallTimer: number | undefined

    const clearStall = () => {
      if (stallTimer !== undefined) window.clearTimeout(stallTimer)
      stallTimer = undefined
      setLoginStalled(false)
    }

    const armStall = () => {
      clearStall()
      stallTimer = window.setTimeout(() => setLoginStalled(true), LOGIN_STALL_MS)
    }

    const requestSignOut = () => {
      frameRef.current?.contentWindow?.postMessage({ carbonCommand: 'logout' }, origin)
    }

    const unregister = registerChatSignOut(
      () =>
        new Promise<void>((resolve) => {
          if (!heardFromScripts) {
            resolve()
            return
          }
          leaving = true
          const timer = window.setTimeout(resolve, SIGN_OUT_WAIT_MS)
          signedOut = () => {
            window.clearTimeout(timer)
            resolve()
          }
          requestSignOut()
        }),
    )

    /*
      🔴 پوستهٔ پنل به قاب — Rocket.Chat روشن/تاریک را از پنل می‌گیرد.
      اسکریپت‌های سفارشیِ Rocket.Chat آن را روی `data-carbon-theme`
      می‌گذارند (infra/rocketchat/). پس از هر پیامِ ورود/خروج دوباره
      فرستاده می‌شود، چون آن پیام یعنی اسکریپت گوش می‌دهد.
    */
    const postTheme = () => {
      frameRef.current?.contentWindow?.postMessage(
        /* `carbonApp`: برنامهٔ نصبی منوی پنل ندارد؛ قاب «≡» ِ گوشی را نشان نمی‌دهد. */
        { carbonTheme: document.documentElement.dataset.theme === 'dark' ? 'dark' : 'light', carbonApp: standalone },
        origin,
      )
    }

    const themeObserver = new MutationObserver(postTheme)
    themeObserver.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] })

    const startLogin = () => {
      if (!service) return

      /* ⚠️ دو فرمانِ پشتِ سرِ هم (startup و load) یک ورود حساب می‌شوند. */
      const now = Date.now()
      if (now - lastStart < 15000) return
      if (attempts() >= MAX_ATTEMPTS || localAttempts >= MAX_ATTEMPTS) return

      lastStart = now
      localAttempts++
      countAttempt()

      frameRef.current?.contentWindow?.postMessage(
        { externalCommand: 'call-custom-oauth-login', service },
        origin,
      )

      /* اگر فرمان بی‌اثر ماند، یک بارِ دیگر — سقفِ تلاش‌ها همچنان حلقه را می‌بندد. */
      if (retryTimer !== undefined) window.clearTimeout(retryTimer)
      retryTimer = window.setTimeout(() => {
        retryTimer = undefined
        if (verified || leaving) return
        lastStart = 0
        startLogin()
      }, LOGIN_RETRY_MS)
    }

    /*
      ==================================================================
      🔴 فرمانِ ورود فقط به Rocket.Chat ِ «آماده» فرستاده می‌شود.

      ⚠️ دیده‌شده ۲۰۲۶-۱۰-۰۵ در لاگِ nginx: پنل فرمان را فرستاد
      (`pf-chat-sso-attempts` = 1) ولی هیچ درخواستی به
      `/oauth/rocketchat/authorize` نرسید. اسکریپتِ «خارج‌شده» زودتر از
      آماده‌شدنِ خودِ Rocket.Chat اجرا می‌شود و فرمانی که آن لحظه برسد
      بی‌صدا دور ریخته می‌شود — و قفلِ ۱۵ثانیه‌ایِ `startLogin` تلاشِ
      بعدی را هم می‌گرفت.

      پس `logged-out` فقط «ورود لازم است» را ثبت می‌کند و فرمان با
      رویدادِ `startup` (که Rocket.Chat پس از آماده‌شدن می‌فرستد) می‌رود.
      اگر `startup` نیامد، پس از چند ثانیه به‌هرحال فرستاده می‌شود.
      ==================================================================
    */
    let rcReady = false
    let loginWanted = false
    let readyFallback: number | undefined
    let retryTimer: number | undefined

    const requestLogin = () => {
      if (rcReady) {
        startLogin()
        return
      }
      loginWanted = true
      if (readyFallback !== undefined) window.clearTimeout(readyFallback)
      readyFallback = window.setTimeout(() => {
        readyFallback = undefined
        if (!loginWanted) return
        loginWanted = false
        startLogin()
      }, READY_FALLBACK_MS)
    }

    const onMessage = (event: MessageEvent) => {
      if (event.origin !== origin) return

      /* درخواستِ کارها و پروژه‌ها — دروازه‌هایش در `rpc/bridge.ts`. */
      if (rpc.handle(event)) return

      /* ⚠️ فقط خودِ قاب — نه پنجرهٔ دیگری از همان دامنه که به پنل دسترسی دارد. */
      if (event.source !== frameRef.current?.contentWindow) return

      const data = event.data as { carbon?: string; emails?: unknown; uid?: unknown; eventName?: string; data?: unknown } | null

      /* گوشی: «≡» ِ نوارِ گفتگو — فقط باز کردنِ منوی پنل، بی هیچ داده‌ای (`shellEvents.ts`) */
      if (data?.carbon === 'open-nav') {
        window.dispatchEvent(new Event(OPEN_NAV_EVENT))
        return
      }

      /*
        🔴 ۱۴۰۵/۰۸/۱۴ دکمهٔ پوسته کنارِ «+» ِ گفتگو (گوشی؛ نوارِ پنل آن‌جا پنهان است) — همان تعویضِ دکمهٔ نوار
        (`theme.ts`)؛ پوستهٔ تازه با themeObserver ِ پایین به خودِ گفتگو برمی‌گردد. بی هیچ داده‌ای: قاب نمی‌گوید کدام.
      */
      if (data?.carbon === 'theme-toggle') {
        switchTheme()
        return
      }

      /* iOS: جای نوشتن فوکوس گرفت یا رها شد (صفحه‌کلید) — اثرِ آیفونِ بالا */
      if (data?.carbon === 'ready') {
        setLayoutReady(true)
        return
      }

      if (data?.carbon === 'keyboard') {
        const open = (data as { open?: unknown }).open === true
        window.dispatchEvent(new CustomEvent<boolean>(CHAT_KEYBOARD_EVENT, { detail: open }))
        return
      }

      /*
        ⚠️ عدد و اعلان فقط پس از تأییدِ کاربرِ داخلِ قاب. پیش‌تر قابِ تازه تا
        اولین پیامِ اسکریپت «مطمئن» بود: روی کامپیوترِ مشترک با نشستِ ماندهٔ
        نفرِ قبلی، عدد و متنِ اعلانِ او به کاربرِ تازه نشان داده می‌شد.
      */
      const untrusted = !verified && !legacy

      if (data?.eventName === 'unread-changed') {
        if (untrusted) {
          pendingUnread = data.data
          return
        }
        setChatUnread(data.data)
        return
      }

      /*
        🔴 اعلانِ پیام — Rocket.Chat خودش نمی‌تواند نشانش بدهد (توضیح
        در `chatNotification.ts`).

        ⚠️ کسی که همین حالا گفتگو را جلوی چشم دارد اعلان نمی‌گیرد —
        پیام را در خودِ Rocket.Chat می‌بیند. پنجرهٔ بی‌تمرکز یا تبِ
        پنهان → اعلانِ دسکتاپ؛ پنل جلوی چشم ولی صفحهٔ دیگر → toast.
      */
      if (data?.eventName === 'notification') {
        if (untrusted) return
        const item = parseChatNotification(data.data)
        if (!item) return

        const { visible: onChat, toast: notify } = live.current
        const seen = document.visibilityState === 'visible'
        const focused = seen && document.hasFocus()
        /* 🔴 ۱۴۰۵/۰۸/۰۷ (عکس‌های آیفون): سافاری hasFocus را برای صفحه‌ای که تمرکز درونِ
           قابِ گفتگوست اغلب false می‌دهد؛ برای همان گفتگوی بازِ جلوی چشم toast می‌آمد و
           روی جای نوشتن می‌نشست. گفتگوی باز و دیده‌شده اعلان نمی‌خواهد. */
        if (onChat && (focused || (seen && item.fromOpenedRoom))) return

        const openRoom = () => openChatRoom(item.path)
        const toastIt = () =>
          notify.toast({
            title: item.title,
            description: item.body || undefined,
            tone: 'info',
            duration: 6000,
            action: { label: 'باز کردنِ گفتگو', onClick: openRoom },
          })

        if (focused) {
          toastIt()
          return
        }
        void showChatNotification(item, openRoom).then((shown) => {
          // روی خودِ صفحهٔ گفتگو toast نه — پایینِ صفحه روی جای نوشتن می‌نشست
          if (!shown && document.visibilityState === 'visible' && !live.current.visible) toastIt()
        })
        return
      }

      if (data?.carbon === 'logged-in') {
        heardFromScripts = true
        rcReady = true
        if (retryTimer !== undefined) window.clearTimeout(retryTimer)
        retryTimer = undefined
        postTheme()

        /* اسکریپتِ قدیمی ایمیل نمی‌فرستد — رفتارِ قبلی. */
        if (Array.isArray(data.emails)) {
          const { expectedEmails, expectedId } = live.current
          const emails = data.emails.map((e) => String(e).toLowerCase())
          const sameAccount = expectedId !== null && typeof data.uid === 'string' && data.uid === expectedId

          if (!sameAccount && !emails.some((e) => expectedEmails.includes(e))) {
            verified = false
            pendingUnread = undefined
            rpc.setVerified(false)
            /* ⚠️ قابِ کاربرِ دیگر هرگز از زیرِ پوشش بیرون نمی‌آید. */
            clearStall()
            setSignedIn(false)
            setChatUnread(0)

            /*
              ⚠️ اگر پس از چند بار خروج و ورود باز هم کسِ دیگری وارد شد
              (مثلاً ایمیلِ حسابِ Rocket.Chat با پنل فرق دارد)، حلقه
              نمی‌سازیم؛ پوشش می‌ماند و پیامِ روشن نشان داده می‌شود.
            */
            if (++mismatches > MAX_ATTEMPTS) {
              setMismatch(true)
              return
            }
            requestSignOut()
            return
          }
        }

        verified = true
        if (pendingUnread !== undefined) {
          setChatUnread(pendingUnread)
          pendingUnread = undefined
        }
        /* ⚠️ RPC فقط با ایمیلِ بررسی‌شده — نه با رفتارِ اسکریپتِ قدیمی. */
        rpc.setVerified(Array.isArray(data.emails))
        clearStall()
        resetAttempts()
        setMismatch(false)
        setSignedIn(true)
        return
      }

      /*
        🔴 «خارج شد» از دو پیامِ جدا می‌آید.

        `signed-out` تأییدِ فرمانِ خروج است و قاب بلافاصله پس از آن
        بارگذاریِ دوباره می‌کند — پس اینجا ورود شروع **نمی‌شود**: فرمانی
        که به صفحهٔ در حالِ رفتن برسد گم می‌شود و تا ۱۵ ثانیه جلوی
        تلاشِ بعدی را هم می‌گیرد. `logged-out` را صفحهٔ تازه می‌فرستد
        و ورود از آنجا شروع می‌شود.
      */
      if (data?.carbon === 'signed-out' || data?.carbon === 'logged-out') {
        heardFromScripts = true
        verified = false
        pendingUnread = undefined
        rpc.setVerified(false)
        signedOut?.()
        signedOut = null
        setSignedIn(false)
        setChatUnread(0)
        if (data.carbon === 'logged-out') {
          postTheme()
          if (!leaving) {
            requestLogin()
            armStall()
          }
        } else {
          /* قاب بارگذاریِ دوباره می‌کند — تا `startup` ِ صفحهٔ تازه، آماده نیست. */
          rcReady = false
          clearStall()
        }
        return
      }

      if (data?.eventName === 'startup') {
        rcReady = true
        if (loginWanted) {
          loginWanted = false
          if (readyFallback !== undefined) window.clearTimeout(readyFallback)
          readyFallback = undefined
          if (!leaving) startLogin()
          return
        }
      }

      /* بی‌اسکریپت: همان رفتارِ قبلی — یک بار در هر زبانه. */
      if (data?.eventName === 'startup' && !heardFromScripts && attempts() === 0) startLogin()
    }

    const frame = frameRef.current
    let loadTimer: number | undefined
    const onLoad = () => {
      loadTimer = window.setTimeout(() => {
        if (!heardFromScripts && attempts() === 0) startLogin()
      }, 1500)
    }

    const overlayTimer = window.setTimeout(() => {
      if (heardFromScripts) return
      legacy = true
      setSignedIn(true)
    }, OVERLAY_FALLBACK_MS)

    /* گفتگوی اعلان — از کلیکِ اعلانِ همین صفحه یا اعلانِ Service Worker (`sw.js`، پیامِ `chat-open`) */
    function openChatRoom(path: string | null) {
      if (!standalone) live.current.navigate('/app/chat')
      if (path) frameRef.current?.contentWindow?.postMessage({ externalCommand: 'go', path }, origin)
    }

    function onWorkerMessage(event: MessageEvent) {
      const data = event.data as { carbon?: string; path?: unknown } | null
      if (data?.carbon !== 'chat-open') return
      openChatRoom(typeof data.path === 'string' ? data.path : null)
    }

    window.addEventListener('message', onMessage)
    navigator.serviceWorker?.addEventListener('message', onWorkerMessage)
    frame?.addEventListener('load', onLoad)

    return () => {
      rpc.dispose()
      unregister()
      if (stallTimer !== undefined) window.clearTimeout(stallTimer)
      if (readyFallback !== undefined) window.clearTimeout(readyFallback)
      if (retryTimer !== undefined) window.clearTimeout(retryTimer)
      themeObserver.disconnect()
      window.removeEventListener('message', onMessage)
      navigator.serviceWorker?.removeEventListener('message', onWorkerMessage)
      frame?.removeEventListener('load', onLoad)
      window.clearTimeout(overlayTimer)
      if (loadTimer !== undefined) window.clearTimeout(loadTimer)
      /* ⚠️ عددِ این کاربر به ورودِ بعدی در همین زبانه نمی‌رسد */
      setChatUnread(0)
    }
  }, [url, service, standalone])

  let body
  if (query.isPending) {
    body = <LoadingState label="در حال اتصال به گفتگو…" />
  } else if (query.isError) {
    body = <ApiErrorState error={query.error} title="گفتگو باز نشد" onRetry={() => void query.refetch()} />
  } else if (!url) {
    body = (
      <EmptyState
        title="گفتگو راه‌اندازی نشده"
        description="سرور گفتگو هنوز به این سامانه وصل نشده است — به مدیر سیستم اطلاع دهید."
      />
    )
  } else {
    /*
      🔴 تمامِ ظرفِ محتوا — بی‌عنوان، بی‌حاشیه.

      ⚠️ حاشیهٔ منفی دقیقاً همان padding ِ ظرف در `AppShell` را خنثی
      می‌کند (`p-4` در موبایل، `--pf-content-pad` از `sm`)؛ اگر آن
      عوض شد، این هم باید عوض شود. گوشه‌های گِرد را خودِ ظرف با
      `overflow-hidden` می‌بُرد، پس قاب حاشیه و گوشهٔ خودش را ندارد.
      ۲px برای حاشیهٔ بالا و پایینِ ظرف کم می‌شود تا صفحه اسکرول نخورد.
      🔴 گوشی: نوارِ پنل پنهان است و ظرف قاب ندارد (`AppShell`)، پس تمامِ صفحه.
    */
    body = (
      <div
        ref={boxRef}
        className={
          standalone
            ? 'relative h-dvh'
            : 'relative -m-4 h-[calc(100dvh-var(--pf-topbar-h)-2px)] min-h-[28rem] sm:-m-[var(--pf-content-pad)] max-md:h-dvh max-md:min-h-0'
        }
      >
        <iframe
          ref={frameRef}
          src={`${url}/home`}
          title="گفتگو"
          className="absolute inset-0 h-full w-full border-0 bg-[var(--pf-surface)]"
          allow="clipboard-write; microphone; camera; display-capture; fullscreen; geolocation"
        />

        {loginOverlay.mounted && (
          <div
            data-state={loginOverlay.state}
            className="pf-crossfade absolute inset-0 flex items-center justify-center bg-[var(--pf-surface)]"
          >
            {mismatch ? (
              <EmptyState
                title="حساب گفتگو با حساب شما یکی نیست"
                description="گفتگو با حساب دیگری باز می‌شود. به مدیر سیستم اطلاع دهید که ایمیل حساب گفتگوی شما را بررسی کند."
              />
            ) : (
              <LoadingState label="در حال ورود به گفتگو…" />
            )}
          </div>
        )}
      </div>
    )
  }

  return <div hidden={!visible}>{body}</div>
}
